import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Badge } from "@/components/ui/badge";
import { ToggleTheme } from "@/components/ui/toggle-theme";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, LogOut, CheckCircle2, ChevronUp, ExternalLink, Activity, Loader2, AlertCircle, Building, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import FloatingDocsModal from "@/components/embed-docs";
import { useState, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { getOrganizations, switchOrganization } from "@/api";
import { useSidebar } from "@/components/ui/sidebar";

interface Organization {
    organization_id: string;
    organization_name: string;
}

export function NavUser() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, setAuthData } = useAuth();
    const { success, error } = useEnhancedToast();
    const { state } = useSidebar();
    const [isOpen, setIsOpen] = useState(false);
    const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [commandKey, setCommandKey] = useState(0);
    const commandListRef = useRef<HTMLDivElement>(null);

    const isCollapsed = state === "collapsed";

    // Helper to navigate to list page if currently on a detail page (with ID)
    const navigateToListPageIfOnDetail = () => {
        const path = location.pathname;
        
        // Extract base route (e.g., /dashboard/assistants from /dashboard/assistants/abc-123)
        // Match: /dashboard/{section}/{id} where id is NOT "new"
        const detailPageMatch = path.match(/^(\/dashboard\/[^/]+)\/((?!new)[^/]+)$/);
        
        if (detailPageMatch) {
            const basePath = detailPageMatch[1]; // e.g., /dashboard/assistants
            navigate(basePath, { replace: true });
            return basePath;
        }
        
        // Special case for workflows (uses subroutes)
        if (path.startsWith('/dashboard/workflows/') && path !== '/dashboard/workflows') {
            navigate('/dashboard/workflows', { replace: true });
            return '/dashboard/workflows';
        }
        
        return null; // Not on a detail page
    };

    const handleLogout = async () => {
        try {
            await logout();
            success({
                icon: CheckCircle2,
                title: "Logged Out Successfully",
                description: "You have been safely logged out of your account.",
                duration: 3000,
            });
            navigate('/');
        } catch (err: unknown) {
            error({
                title: "Logout Failed",
                description: err instanceof Error ? err.message : "There was an issue logging you out. Please try again.",
                duration: 4000,
            });
        }
    };

    // Fetch organizations on mount to show current org name
    useEffect(() => {
        const fetchOrganizations = async () => {
            setIsLoadingOrgs(true);
            try {
                const response = await getOrganizations();
                if (response.error) {
                    throw new Error(response.error);
                }
                const orgs = response.data ?? [];
                
                // Sort: current org first, then alphabetically
                const sortedOrgs = [...orgs].sort((a, b) => {
                    if (a.organization_id === user?.organization_id) return -1;
                    if (b.organization_id === user?.organization_id) return 1;
                    return a.organization_name.localeCompare(b.organization_name);
                });
                
                setOrganizations(sortedOrgs);
            } catch (err) {
                // Silently fail on initial load
                console.error('Failed to load organizations:', err);
            } finally {
                setIsLoadingOrgs(false);
            }
        };

        fetchOrganizations();
    }, [user?.organization_id]);

    // Show error only when user opens the dialog
    useEffect(() => {
        if (isOrgDialogOpen && organizations.length === 0 && !isLoadingOrgs) {
            error({
                icon: AlertCircle,
                title: "Failed to Load Organizations",
                description: "Unable to load organizations. Please try again.",
                duration: 4000,
            });
        }
    }, [isOrgDialogOpen, organizations.length, isLoadingOrgs, error]);

    // Keyboard shortcuts: Cmd/Ctrl+O or Cmd/Ctrl+E (toggle)
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if ((e.key === "o" || e.key === "e") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setIsOrgDialogOpen((prev) => !prev);
            }
        };

        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, []);

    // Disable scrollIntoView when organization dialog is open (only for initial auto-scroll)
    useEffect(() => {
        if (!isOrgDialogOpen) return;

        let isInitialRender = true;
        const originalScrollIntoView = Element.prototype.scrollIntoView;
        
        Element.prototype.scrollIntoView = function(arg?: boolean | ScrollIntoViewOptions) {
            // Allow scrolling after initial render (for keyboard navigation)
            if (!isInitialRender) {
                originalScrollIntoView.call(this, arg);
            }
            // Block initial auto-scroll
        };

        // After a short delay, allow normal scrolling (for keyboard navigation)
        const timer = setTimeout(() => {
            isInitialRender = false;
        }, 100);

        return () => {
            clearTimeout(timer);
            Element.prototype.scrollIntoView = originalScrollIntoView;
        };
    }, [isOrgDialogOpen, commandKey]);

    // Reset search value when dialog closes and focus input when it opens
    useEffect(() => {
        if (!isOrgDialogOpen) {
            setSearchValue('');
        } else {
            // Focus the search input when dialog opens (after animation)
            // Longer delay to ensure dropdown has closed and dialog has animated in
            setTimeout(() => {
                const input = document.querySelector('[cmdk-input]') as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            }, 200);
        }
    }, [isOrgDialogOpen]);

    const handleOrganizationSwitch = async (org: Organization) => {
        if (org.organization_id === user?.organization_id) {
            setIsOrgDialogOpen(false);
            return;
        }

        setIsSwitching(true);
        try {
            const result = await switchOrganization(org.organization_id);

            if (!result.error) {
                // Navigate to list page if on detail page (smooth UX - stay in same section)
                navigateToListPageIfOnDetail();
                
                // Refresh user data
                await setAuthData();
                
                setIsOrgDialogOpen(false);
                success({
                    icon: Building,
                    title: "Organization Switched",
                    description: `Successfully switched to ${org.organization_name}`,
                    duration: 3000,
                });
            } else {
                throw new Error('Failed to switch organization');
            }
        } catch (err: unknown) {
            error({
                icon: AlertCircle,
                title: "Switch Failed",
                description: err instanceof Error ? err.message : "Failed to switch organization. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsSwitching(false);
        }
    };

    const userInitials = user?.first_name && user?.last_name
        ? `${user?.first_name[0]}${user?.last_name[0]}`.toUpperCase()
        : '?';

    const displayName = user?.first_name && user?.last_name
        ? `${user?.first_name} ${user?.last_name}`
        : user?.email?.split('@')[0] || 'User';

    // Get current organization name
    const currentOrgName = organizations.find(org => org.organization_id === user?.organization_id)?.organization_name || user?.organization_name || 'Organization';

    // Memoize organization data with pre-computed search fields to avoid recalculating on every filter
    const organizationsWithInitials = useMemo(() => {
        return organizations.map(org => {
            const words = org.organization_name.toLowerCase().split(' ');
            return {
                ...org,
                initials: org.organization_name
                    .split(' ')
                    .map(word => word[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join('')
                    .toUpperCase(),
                // Pre-compute lowercase values for search
                nameLower: org.organization_name.toLowerCase(),
                nameWords: words
            };
        });
    }, [organizations]);

    return (
        <>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                        <button className={cn(
                            "w-full rounded-lg relative overflow-hidden group",
                            "transition-all duration-300 ease-in-out",
                            isCollapsed 
                                ? "p-0 bg-transparent hover:bg-transparent border-0 shadow-none flex items-center justify-center" 
                                : "p-2 border border-border shadow-sm bg-card hover:bg-accent/50 hover:shadow-md",
                            isOpen && !isCollapsed ? "ring-2 ring-primary/20 ring-offset-1 ring-offset-background shadow-md" : ""
                        )}>
                            <div className={cn(
                                "flex items-center relative transition-all duration-300 ease-in-out",
                                isCollapsed ? "justify-center w-auto" : "justify-between w-full"
                            )}>
                                <div className={cn(
                                    "flex items-center min-w-0 transition-all duration-300 ease-in-out",
                                    isCollapsed ? "gap-0 justify-center" : "gap-2.5 flex-1 mr-2"
                                )}>
                                    <Avatar className={cn(
                                        "rounded-lg border-2 flex-shrink-0 transition-all duration-300 ease-in-out",
                                        isCollapsed 
                                            ? "h-8 w-8 border-primary/20 shadow-sm hover:shadow-md hover:border-primary/30 cursor-pointer" 
                                            : "h-9 w-9 border-primary/20 shadow-sm"
                                    )}>
                                        <AvatarFallback className={cn(
                                            "rounded-[6px] bg-primary/10 text-primary font-semibold transition-all duration-300 ease-in-out",
                                            isCollapsed ? "text-xs" : "text-sm"
                                        )}>
                                            {userInitials}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className={cn(
                                        "flex flex-col justify-center min-w-0 text-left transition-all duration-300 ease-in-out",
                                        isCollapsed ? "opacity-0 w-0 overflow-hidden pointer-events-none" : "opacity-100 flex-1"
                                    )}>
                                        <div className="text-sm font-semibold truncate leading-tight text-foreground max-w-full">
                                            {displayName}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate mt-0.5 max-w-full">
                                            {currentOrgName}
                                        </div>
                                    </div>
                                </div>
                                <ChevronUp className={cn(
                                    "h-4 w-4 text-muted-foreground flex-shrink-0 transition-all duration-300 ease-in-out",
                                    isOpen ? 'rotate-0' : 'rotate-180',
                                    isCollapsed ? "opacity-0 w-0 overflow-hidden pointer-events-none absolute" : "opacity-100"
                                )} />
                            </div>
                        </button>
                    </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent
                    side="right"
                    align="center"
                    hidden={!isCollapsed}
                    className="font-normal"
                >
                    <div className="text-sm font-semibold">{displayName}</div>
                    <div className="text-xs text-muted-foreground">{currentOrgName}</div>
                </TooltipContent>
            </Tooltip>
            <DropdownMenuContent 
                className="w-[260px] p-0 border border-border shadow-lg rounded-lg overflow-hidden bg-card animate-in fade-in-50 slide-in-from-left-2 duration-200" 
                side="right"
                align="end" 
                forceMount
                sideOffset={8}
                alignOffset={0}
            >
                {/* User Info Header */}
                <DropdownMenuLabel className="font-normal px-4 py-3 border-b border-border bg-muted/30 cursor-default">
                    <div className="flex items-center gap-3 select-none">
                        <Avatar className="h-9 w-9 rounded-lg border-2 border-primary/20 shadow-sm flex-shrink-0">
                            <AvatarFallback className="rounded-[6px] bg-primary/10 text-primary font-semibold text-sm">
                                {userInitials}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0 flex-1">
                            <p className="text-sm font-semibold text-foreground truncate">
                                {displayName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                </DropdownMenuLabel>
                
                {/* Organization Section */}
                <div className="flex items-center justify-between px-3 py-1 bg-muted/30">
                    <div className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                        Organization
                    </div>
                    <div className="flex items-center gap-1">
                        <KbdGroup>
                            <Kbd>⌘</Kbd>
                            <Kbd>O</Kbd>
                        </KbdGroup>
                        <span className="text-xs text-muted-foreground/50">/</span>
                        <KbdGroup>
                            <Kbd>⌘</Kbd>
                            <Kbd>E</Kbd>
                        </KbdGroup>
                    </div>
                </div>
                <DropdownMenuSeparator className="m-0 bg-border" />
                <div className="px-2 py-1">
                    <button
                        onClick={() => {
                            setIsOpen(false); // Close dropdown first
                            // Open dialog after dropdown closes
                            setTimeout(() => {
                                setIsOrgDialogOpen(true);
                            }, 50);
                        }}
                        className={cn(
                            "w-full h-9 flex items-center justify-between gap-2",
                            "px-3 rounded-md",
                            "border border-border bg-muted/40",
                            "hover:bg-muted/60 hover:border-border transition-colors",
                            "cursor-pointer"
                        )}
                    >
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <div className="flex-shrink-0 h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
                                <Building className="h-3.5 w-3.5 text-primary" />
                            </div>
                            <span className="text-sm font-medium truncate text-foreground">
                                {isLoadingOrgs ? (
                                    <span className="flex items-center gap-1.5">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Loading...
                                    </span>
                                ) : (
                                    organizations.find(org => org.organization_id === user?.organization_id)?.organization_name || 'Select Organization'
                                )}
                            </span>
                        </div>
                        <ChevronUp className="h-4 w-4 text-muted-foreground rotate-180 flex-shrink-0" />
                    </button>
                </div>
                
                <DropdownMenuSeparator className="m-0 bg-border" />
                
                {/* Account Section */}
                <div className="px-3 py-1 bg-muted/30 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                    Account
                </div>
                <DropdownMenuSeparator className="m-0 bg-border" />
                <div className="py-1">
                    <DropdownMenuGroup>
                        <DropdownMenuItem 
                            onClick={() => navigate('/dashboard/settings')} 
                            className="cursor-pointer mx-2 rounded-md px-3 py-1.5 gap-3 focus:bg-accent"
                        >
                            <Settings className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm flex-1">Settings</span>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </div>
                
                <DropdownMenuSeparator className="m-0 bg-border" />
                
                {/* Appearance Section */}
                <div className="px-3 py-1 bg-muted/30 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                    Appearance
                </div>
                <DropdownMenuSeparator className="m-0 bg-border" />
                <div className="py-1">
                    <div className="mx-2 px-3 py-1.5 flex items-center justify-between gap-3 cursor-default">
                        <span className="text-sm text-foreground select-none">Theme</span>
                        <ToggleTheme />
                    </div>
                </div>
                
                <DropdownMenuSeparator className="m-0 bg-border" />
                
                {/* Resources Section */}
                <div className="px-3 py-1 bg-muted/30 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                    Resources
                </div>
                <DropdownMenuSeparator className="m-0 bg-border" />
                <div className="py-1">
                    <DropdownMenuGroup>
                        <DropdownMenuItem 
                            className="p-0 mx-2 cursor-pointer focus:bg-accent rounded-md"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <div className="w-full [&_button]:w-full [&_button]:justify-start [&_button]:px-3 [&_button]:py-1.5 [&_button]:gap-3 [&_button]:text-sm [&_button]:font-normal [&_button]:hover:bg-transparent [&_svg]:text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
                                <FloatingDocsModal />
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            className="p-0 mx-2 cursor-pointer focus:bg-accent rounded-md"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <div className="w-full [&_button]:w-full [&_button]:justify-start [&_button]:px-3 [&_button]:py-1.5 [&_button]:gap-3 [&_button]:text-sm [&_button]:font-normal [&_button]:hover:bg-transparent [&_svg]:text-muted-foreground [&_svg]:h-4 [&_svg]:w-4">
                                <FloatingDocsModal 
                                    path="/api-reference" 
                                    icon="api" 
                                    label="API Reference"
                                />
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            asChild 
                            className="cursor-pointer mx-2 rounded-md px-3 py-1.5 gap-3 focus:bg-accent"
                        >
                            <a 
                                href="https://status.voicebot.studio" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center"
                            >
                                <Activity className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm flex-1">System Status</span>
                                <ExternalLink className="h-3 w-3 text-muted-foreground/50" />
                            </a>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </div>
                
                <DropdownMenuSeparator className="m-0 bg-border" />
                
                {/* Logout */}
                <div className="pt-1 pb-1">
                    <DropdownMenuItem 
                        onClick={handleLogout} 
                        className="cursor-pointer mx-2 rounded-md px-3 py-1.5 gap-3 text-destructive focus:bg-destructive/10 focus:text-destructive"
                    >
                        <LogOut className="h-4 w-4" />
                        <span className="text-sm font-medium flex-1">Log out</span>
                    </DropdownMenuItem>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>

        {/* Organization Switcher Dialog */}
        <Dialog open={isOrgDialogOpen} onOpenChange={setIsOrgDialogOpen}>
            <DialogContent className="p-0 gap-0 max-w-xl [&>button]:hidden">
                <VisuallyHidden>
                    <DialogTitle>Switch Organization</DialogTitle>
                    <DialogDescription>
                        Search and switch to a different organization
                    </DialogDescription>
                </VisuallyHidden>
                <Command 
                    key={commandKey}
                    className="rounded-lg"
                    filter={(_value, search, keywords) => {
                        // If search is empty, show all items
                        if (!search || search.trim() === '') {
                            return 1;
                        }
                        
                        if (!keywords || keywords.length === 0) return 0;
                        
                        const searchLower = search.toLowerCase();
                        // keywords: [orgName, initials, nameLower, ...nameWords]
                        const initials = (keywords[1] || '').toLowerCase();
                        const nameWords = keywords.slice(3); // Pre-computed lowercase words
                        
                        // PRIORITY 1: Exact initials match (highest score)
                        if (initials === searchLower) return 1.0;
                        
                        // PRIORITY 2: Initials start with search
                        if (initials.startsWith(searchLower)) return 0.9;
                        
                        // PRIORITY 3: Org name word starts with search (no split needed!)
                        if (nameWords.some(word => word.startsWith(searchLower))) return 0.8;
                        
                        // PRIORITY 4: Org name word contains search (no split needed!)
                        if (nameWords.some(word => word.includes(searchLower))) return 0.7;
                        
                        // No match
                        return 0;
                    }}
                >
                    <CommandInput 
                        placeholder="Search organizations..." 
                        showEsc={true}
                        value={searchValue}
                        autoFocus
                        onValueChange={(value) => {
                            // If clearing search (going from non-empty to empty), force remount
                            if (searchValue !== '' && value === '') {
                                setCommandKey(prev => prev + 1);
                            }
                            
                            setSearchValue(value);
                            // Reset scroll to top when search changes (use ref instead of querySelector)
                            if (commandListRef.current) {
                                commandListRef.current.scrollTop = 0;
                            }
                        }}
                    />
                    <CommandList ref={commandListRef} className="min-h-[300px] max-h-[400px]">
                        <CommandEmpty className="flex items-center justify-center min-h-[300px]">
                            {isLoadingOrgs ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
                                    <p className="text-sm text-muted-foreground">Loading organizations...</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="rounded-full bg-muted p-3 mb-3">
                                        <Search className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <p className="text-sm font-medium text-foreground mb-1">No organizations found</p>
                                    <p className="text-xs text-muted-foreground">Try adjusting your search</p>
                                </div>
                            )}
                        </CommandEmpty>
                        <CommandGroup heading="Your Organizations">
                            {organizationsWithInitials.map((org) => (
                                <CommandItem
                                    key={org.organization_id}
                                    value={org.organization_id}
                                    keywords={[org.organization_name, org.initials, org.nameLower, ...org.nameWords]}
                                    onSelect={() => handleOrganizationSwitch(org)}
                                    disabled={isSwitching}
                                    className="cursor-pointer"
                                >
                                    <div className="flex items-center gap-3 w-full">
                                        <div className={cn(
                                            "flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-semibold text-xs",
                                            user?.organization_id === org.organization_id
                                                ? "bg-primary/10 text-primary border-2 border-primary/20"
                                                : "bg-muted text-muted-foreground border-2 border-border/50"
                                        )}>
                                            {isSwitching && user?.organization_id === org.organization_id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                org.initials
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 flex items-center gap-2">
                                            <p className="text-sm font-medium truncate">
                                                {org.organization_name}
                                            </p>
                                            {user?.organization_id === org.organization_id && (
                                                <Badge 
                                                    variant="outline" 
                                                    className="flex-shrink-0 text-[10px] px-1.5 py-0 h-5 bg-primary/10 text-primary border-primary/20"
                                                >
                                                    Current
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    
                    {/* Footer with keyboard shortcuts */}
                    <div className="border-t bg-muted/30 px-4 py-2.5 flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5">
                                <KbdGroup>
                                    <Kbd>↑</Kbd>
                                    <Kbd>↓</Kbd>
                                </KbdGroup>
                                <span>Navigate</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Kbd>Enter</Kbd>
                                <span>Select</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Kbd>ESC</Kbd>
                            <span>Close</span>
                        </div>
                    </div>
                </Command>
            </DialogContent>
        </Dialog>
        </>
    );
}

